import { Eye, EyeOff, Minus, Plus } from "lucide-react";
import * as React from "react";
import { cn } from "~/lib/utils";

function Input({
  className,
  inputClassName,
  size = "default",
  type,
  iconStart,
  iconEnd,
  numberControls,
  variant = "outline",
  suffix,
  prefix,
  onIncrease,
  onDecrease,
  disablePasswordManagers,
  ...props
}: Omit<React.ComponentProps<"input">, "size"> & {
  inputClassName?: string;
  size?: "large" | "default";
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
  numberControls?: boolean;
  variant?: "outline" | "flat";
  suffix?: string;
  prefix?: React.ReactNode;
  onIncrease?: () => void;
  onDecrease?: () => void;
  disablePasswordManagers?: boolean;
}) {
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const isPassword = type === "password";
  const showEndAffordance = Boolean(iconEnd) || isPassword;
  const inputType = isPassword && passwordVisible ? "text" : type;

  const variants = {
    outline: "border-2 border-black bg-white/50",
    flat: "border border-input shadow-xs",
  };

  return (
    <div className={cn("relative flex items-center", className)}>
      {iconStart && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-black pointer-events-none z-10 [&_svg]:w-5 [&_svg]:h-5">
          {iconStart}
        </div>
      )}
      {prefix && (
        <div
          className={cn(
            "bg-gray-darker text-white px-3 h-10 flex items-center justify-center rounded-l-md shrink-0",
            variant === "outline"
              ? "border-2 border-r-0 border-black"
              : "border border-r-0 border-input",
          )}
        >
          {prefix}
        </div>
      )}
      <div className="relative flex-1 min-w-0">
        <input
          type={inputType}
          data-slot="input"
          autoComplete={disablePasswordManagers ? "off" : props.autoComplete}
          data-1p-ignore={disablePasswordManagers ? true : undefined}
          data-lpignore={disablePasswordManagers ? "true" : undefined}
          data-form-type={disablePasswordManagers ? "other" : undefined}
          className={cn(
            "text-black file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground w-full min-w-0 bg-white py-1 transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-white file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
            "aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
            "autofill:shadow-[inset_0_0_0px_1000px_rgb(242,242,242)] autofill:[-webkit-text-fill-color:var(--color-gray-900)]",
            variants[variant],
            prefix ? "rounded-l-none border-l-0" : "rounded-l-md",
            suffix ? "rounded-r-none border-r-0" : "rounded-r-md",
            iconStart
              ? size === "large"
                ? "pl-12!"
                : "pl-10!"
              : size === "large"
                ? "pl-4"
                : "pl-3",
            showEndAffordance ? "pr-10!" : suffix ? "" : "pr-3",
            numberControls
              ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none text-center"
              : "",
            size === "large"
              ? "h-12 text-lg md:text-base"
              : "h-10 text-base md:text-sm",
            inputClassName,
          )}
          {...props}
        />
        {isPassword ? (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-black cursor-pointer rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 [&_svg]:w-5 [&_svg]:h-5"
            onClick={() => setPasswordVisible((visible) => !visible)}
            aria-label={
              passwordVisible ? "Passwort verbergen" : "Passwort anzeigen"
            }
            aria-pressed={passwordVisible}
            disabled={props.disabled}
          >
            {passwordVisible ? <EyeOff /> : <Eye />}
          </button>
        ) : (
          iconEnd && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-black pointer-events-none z-10 [&_svg]:w-5 [&_svg]:h-5">
              {iconEnd}
            </div>
          )
        )}
      </div>
      {suffix && (
        <div
          className={cn(
            "bg-gray-darker text-white px-4 h-10 flex items-center justify-center rounded-r-md shrink-0",
            variant === "outline"
              ? "border-2 border-l-0 border-black"
              : "border border-l-0 border-input",
          )}
        >
          {suffix}
        </div>
      )}
      {numberControls && (
        <>
          <button
            type="button"
            className="bg-neutral-darker/10 rounded-md absolute left-2 cursor-pointer"
            onClick={onDecrease}
          >
            <Minus />
          </button>
          <button
            type="button"
            className="bg-neutral-darker/10 rounded-md absolute right-2 cursor-pointer"
            onClick={onIncrease}
          >
            <Plus />
          </button>
        </>
      )}
    </div>
  );
}

export { Input };
